import { DeviceDTO, DeviceShadowDTO } from '@eppendorf/vnls-device-service-utils';
import { Icon, IconSizeClasses, SortableTable } from '@eppendorf/vnls-react-components';
import { ButtonV2, useBreakpoint } from '@eppendorf/vnls-react-components-preview';
import { createColumnHelper } from '@tanstack/react-table';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteError, useSearchParams } from 'react-router-dom';

import { AccessControlGuard } from '$shared/access-control/access-control-guard';
import { useAccessControlGuard } from '$shared/custom-hooks/useAccessControlGuard';
import type { Pagination as PaginationType } from '$shared/types';

import { DeviceNameOrSerialNumber } from '$components/device/device-name-or-serialnumber/device-name-or-serialnumber';
import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { Overview } from '$components/overview-box/overview-box';
import { SearchInput } from '$components/search-input/search-input';
import { NotEnoughDevicesDialog } from '$components/subscription-dialog/subscription-dialog-not-enough-devices';
import { Pagination } from '$components/table/pagination/pagination';
import { DeviceDialogWrapper } from '$features/add-device';
import { DeleteDeviceConfirmWrapper } from '$features/device-list/delete-device-confirm-wrapper';
import { DeviceStatus } from '$features/device-list/device-status';
import { DeviceTypeCell } from '$features/device-list/device-type-cell';
import { useAddedDevices, useGetDeviceUsage } from '$features/devices/devices.api';
import { useDevicesV2WithQueryParams } from '$features/monitoring/v2/devicesv2.api';
import { isValueWithKeyInSearchParams } from '$features/navigation/isValueWithKeyInSearchParams';
import { useGetSubscription } from '$features/subscription/subscription-api';

import './device-list.scss';

export function DeviceList(): ReactElement {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const navigate = useNavigate();

  const isMinSm = useBreakpoint({ min: 'sm' });
  const isMaxMd = useBreakpoint({ max: 'md' });

  const routeError = useRouteError();
  const hasActionsPermission = useAccessControlGuard('DEREGISTER_DEVICE');
  const [{ page, pageSize }, setPagination] = useState<PaginationType>({
    page: 1,
    pageSize: 10,
  });

  const handleSearchQueryChange = useCallback(
    (newSearchQuery: string) => {
      if (searchQuery === newSearchQuery) {
        return;
      }
      setSearchQuery(newSearchQuery?.length > 0 ? newSearchQuery : '');
      setPagination((prev) => ({
        ...prev,
        page: 1,
      }));
    },
    [searchQuery, setSearchQuery, setPagination],
  );

  const [selectedDevice, setSelectedDevice] = useState<DeviceDTO>();

  const { t } = useTranslation();

  const {
    data: paginatedData,
    isError,
    isLoading,
  } = useDevicesV2WithQueryParams({
    page,
    pageSize,
    searchQuery: searchQuery || '',
  });

  const { data: addQueryState } = useAddedDevices();

  const devices = paginatedData ? paginatedData.data : [];
  const [isDeviceDialogOpen, setIsDeviceDialogOpen] = useState(false);
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);
  const [isDeleteDeviceDialogOpen, setIsDeleteDeviceDialogOpen] = useState(false);

  useEffect(() => {
    if (addQueryState && addQueryState.length > 0) {
      setPagination({ pageSize, page: 1 });
    }
  }, [addQueryState]);

  function handleRowClick(device: DeviceDTO) {
    navigate({
      search: `?sidecarRoute=device/details&manufacturer=${device.manufacturer}&serialNumber=${device.serialNumber}`,
    });
  }

  const handlePageSizeChange = (newPageSize: string) => {
    setPagination({ page: 1, pageSize: Number(newPageSize) });
  };

  const handlePageChange = (newPage: number) => {
    setPagination({ pageSize, page: newPage });
  };

  function handleDeleteDevice(device: DeviceDTO) {
    setSelectedDevice(device);
    setIsDeleteDeviceDialogOpen(true);
  }

  const columnHelper = createColumnHelper<DeviceDTO & DeviceShadowDTO>();
  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor('activityState', {
        header: () => t('shared.status'),
        meta: { dataLabel: t('shared.status') },
        // eslint-disable-next-line react/no-unstable-nested-components -- should be fine
        cell: (info) => (
          <div className="realtime-cell">
            <DeviceStatus
              unitState={info.getValue()}
              connectionStatus={info.row?.original?.connectionStatus}
            />
          </div>
        ),
      }),
      columnHelper.accessor('type', {
        header: () => t('deviceTable.type'),
        meta: { dataLabel: t('deviceTable.type') },
        // eslint-disable-next-line react/no-unstable-nested-components -- should be fine
        cell: (info) => <DeviceTypeCell device={info.row.original} withModel />,
      }),
      columnHelper.accessor('name', {
        header: () => t('deviceTable.name'),
        meta: { dataLabel: t('deviceTable.name') },
        // eslint-disable-next-line react/no-unstable-nested-components -- should be fine
        cell: (info) => <DeviceNameOrSerialNumber device={info.row.original} />,
      }),
      columnHelper.accessor('model', {
        header: () => t('deviceTable.model'),
        meta: { dataLabel: t('deviceTable.model') },
        cell: (info) => info.renderValue(),
      }),
      columnHelper.accessor('serialNumber', {
        header: () => t('devices.serialNumber'),
        meta: { dataLabel: t('devices.serialNumber') },
        cell: (info) => info.renderValue(),
      }),
      columnHelper.accessor('registrationNumber', {
        header: () => t('devices.inventoryId'),
        meta: { dataLabel: t('devices.inventoryId') },
        cell: (info) => info.renderValue(),
      }),
      // using same accessor means you have to manually define unique ids (if no ids set, then
      // the accessor name is used for id and this results in errors if using same accessore multiple times)
      columnHelper.accessor('location', {
        header: () => t('deviceDetail.deviceInformation.site'),
        meta: { dataLabel: t('deviceDetail.deviceInformation.site') },
        cell: (info) => info.getValue()?.site,
        id: 'site',
      }),
      columnHelper.accessor('location', {
        header: () => t('deviceDetail.deviceInformation.building'),
        meta: { dataLabel: t('deviceDetail.deviceInformation.building') },
        cell: (info) => info.getValue()?.building,
        id: 'building',
      }),
      columnHelper.accessor('location', {
        header: () => t('deviceDetail.deviceInformation.room'),
        meta: { dataLabel: t('deviceDetail.deviceInformation.room') },
        cell: (info) => info.getValue()?.room,
        id: 'room',
      }),
    ];

    const actionColumn = columnHelper.display({
      id: 'actions',
      header: () => t('shared.actions'),
      // eslint-disable-next-line react/no-unstable-nested-components -- accepted
      cell: ({ row }) => (
        // The problem: When rendering the Dialog multiple times for each row,
        // the Dialog re-renders (=closes itself) when the row is re-rendered
        // (e.g. when /devices are being refetched)
        // The solution: We render the Dialog only once, and control it via state.
        <ButtonV2
          iconOnly
          variant="ghost"
          onClick={(e) => e.stopPropagation()}
          onPress={() => handleDeleteDevice(row.original)}
          aria-label={t('shared.delete')}
        >
          <Icon name="delete" size={IconSizeClasses.XSmall} />
        </ButtonV2>
      ),
    });

    // since no other actions are defined yet we won't show action colum at all if the following condition is false
    if (hasActionsPermission) {
      return [...cols, actionColumn];
    }
    return cols;
  }, [hasActionsPermission, t]);

  const [searchParams] = useSearchParams();

  const { data: subscriptionData, isLoading: isSubscriptionLoading } =
    useGetSubscription();
  const { data: deviceUsageData, isLoading: isDeviceUsageLoading } = useGetDeviceUsage();

  const atLimit =
    !isSubscriptionLoading &&
    !isDeviceUsageLoading &&
    subscriptionData?.subscribedDeviceCount !== undefined &&
    deviceUsageData?.usedDeviceCount !== undefined &&
    deviceUsageData.usedDeviceCount >= subscriptionData.subscribedDeviceCount;

  const onClickAddDevice = () => {
    if (atLimit) {
      setIsSubscriptionDialogOpen(true);
      return;
    }
    setIsDeviceDialogOpen(true);
  };

  const isAddButtonDisabled = isDeviceUsageLoading || isSubscriptionLoading;

  return (
    <Overview className={`box--mobile-fullscreen ${isMaxMd ? 'box--no-shadow' : ''}`}>
      {isDeviceDialogOpen && (
        <DeviceDialogWrapper
          isOpen={isDeviceDialogOpen}
          onOpenChange={(open) => setIsDeviceDialogOpen(open)}
        />
      )}
      <DeleteDeviceConfirmWrapper
        isDeleteConfirmOpen={isDeleteDeviceDialogOpen}
        onOpenChange={(open) => setIsDeleteDeviceDialogOpen(open)}
        selectedDevice={selectedDevice}
      />
      <NotEnoughDevicesDialog
        isOpen={isSubscriptionDialogOpen}
        onOpenChange={setIsSubscriptionDialogOpen}
        licensedDevices={subscriptionData?.subscribedDeviceCount}
        usedDevices={deviceUsageData?.usedDeviceCount}
      />

      <Overview.Header>
        <div className="device-list__header">
          <h1 className="device-list__title title">{t('devices.overview')}</h1>

          <SearchInput
            onSearchQueryChange={handleSearchQueryChange}
            placeholder={t('devices.searchPlaceholder')}
            id="device-list-search"
            className="device-list__search-wrapper"
          />
          <AccessControlGuard requiredPermissions="ADD_DEVICE">
            <ButtonV2
              variant="primary"
              className={`device-list__add-device ${isMinSm ? 'p-left-sm' : ''}`}
              onPress={() => onClickAddDevice()}
              disabled={isAddButtonDisabled}
              iconOnly={!isMinSm}
            >
              <Icon name="plus" size={IconSizeClasses.XSmall} className="bg-white" />
              {isMinSm && t('addDevice.add')}
            </ButtonV2>
          </AccessControlGuard>
        </div>
      </Overview.Header>
      <Overview.Body>
        {(routeError || isError) && <LoadingErrorHint />}

        {!isLoading && !isError && (
          <>
            {paginatedData &&
              !isLoading &&
              paginatedData?.pagination &&
              devices?.length > 0 && (
                <>
                  <SortableTable<
                    DeviceDTO & DeviceShadowDTO[],
                    DeviceDTO & DeviceShadowDTO
                  >
                    data={devices}
                    columns={columns}
                    onRowClick={(device) => handleRowClick(device)}
                    isSelectedRow={(device) =>
                      isValueWithKeyInSearchParams(
                        'serialNumber',
                        device.serialNumber,
                        searchParams,
                      )
                    }
                    isTableToCardDesign
                    className="table--device-list"
                  />
                  <Pagination
                    paginationData={paginatedData.pagination}
                    onPageSizeChange={(size) => handlePageSizeChange(size)}
                    onPageChange={(newPage) => handlePageChange(newPage)}
                  />
                </>
              )}

            {!isLoading && !isError && (!devices || devices.length === 0) && (
              <div className="box flex flex__dir--column flex__ai--center p-xxxl">
                <Icon
                  name={searchQuery.length ? 'no-results' : 'generic'}
                  size={IconSizeClasses.Large}
                  className="bg-gray-500 m-bottom-l"
                />

                <p className="m-bottom-m font-weight-medium font-size-xl">
                  {searchQuery?.length > 0
                    ? t('devices.emptySearch')
                    : t('deviceTable.emptyState')}
                </p>
                <p className="m-bottom-xxl w-max-xl text-align-center">
                  {searchQuery?.length > 0
                    ? t('shared.tryAdjustSearchCriteria')
                    : t('deviceTable.emptyStateHint')}
                </p>
                {!searchQuery?.length && (
                  <ButtonV2
                    size="md"
                    onPress={() => onClickAddDevice()}
                    disabled={isAddButtonDisabled}
                    className="p-left-sm"
                  >
                    <Icon
                      name="plus"
                      className="bg-white m-right-xs"
                      size={IconSizeClasses.XSmall}
                    />
                    {t('addDevice.add')}
                  </ButtonV2>
                )}
              </div>
            )}
          </>
        )}
      </Overview.Body>
    </Overview>
  );
}
